import {
  ApiError,
  type ImportantFlag,
  type ImportantItemType,
  type ImportantRecipient,
  queryKeys,
} from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Star, X } from 'lucide-react'
import { useState } from 'react'
import { api } from '../lib/api.js'

/**
 * "Mark important" on an item page (t1377): pick colleagues and the item shows
 * in an Important box at the top of their dashboard until they clear it.
 */
export function MarkImportantButton({
  itemType,
  itemId,
}: {
  itemType: ImportantItemType
  itemId: string
}) {
  const [open, setOpen] = useState(false)
  const { data: flags } = useQuery({
    queryKey: queryKeys.important.forItem(itemType, itemId),
    queryFn: () => api.get<ImportantFlag[]>('/important', { query: { itemType, itemId } }),
  })
  const count = flags?.length ?? 0

  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        <Star className="size-3.5" aria-hidden="true" /> Mark important
        {count > 0 ? ` (${count})` : ''}
      </Button>
      {open ? (
        <MarkImportantDialog
          itemType={itemType}
          itemId={itemId}
          flags={flags ?? []}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  )
}

function MarkImportantDialog({
  itemType,
  itemId,
  flags,
  onClose,
}: {
  itemType: ImportantItemType
  itemId: string
  flags: ImportantFlag[]
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [note, setNote] = useState('')

  const { data: recipients, isLoading } = useQuery({
    queryKey: queryKeys.important.recipients(),
    queryFn: () => api.get<ImportantRecipient[]>('/important/recipients'),
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.important.all() })

  const save = useMutation({
    mutationFn: () =>
      api.post<ImportantFlag[]>('/important', {
        itemType,
        itemId,
        recipientIds: [...picked],
        note: note.trim() || undefined,
      }),
    onSuccess: async () => {
      await refresh()
      toast.success('Marked important')
      onClose()
    },
    onError: (error: Error) =>
      toast.error(
        'Could not mark this important',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const remove = useMutation({
    mutationFn: (flagId: string) => api.post(`/important/${flagId}/dismiss`),
    onSuccess: async () => {
      await refresh()
      toast.success('Removed')
    },
    onError: (error: Error) =>
      toast.error('Could not remove it', error instanceof ApiError ? error.message : undefined),
  })

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark important for…</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {flags.length > 0 ? (
            <div>
              <p className="mb-1 text-xs font-medium text-foreground-muted">
                Already important for
              </p>
              <ul className="space-y-1">
                {flags.map((f) => (
                  <li key={f.id} className="flex items-center justify-between text-sm">
                    <span>{f.recipientName}</span>
                    <button
                      type="button"
                      className="rounded p-1 text-foreground-muted hover:bg-surface-sunken"
                      aria-label={`Remove for ${f.recipientName}`}
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(f.id)}
                    >
                      <X className="size-3.5" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div>
            <p className="mb-1 text-xs font-medium text-foreground-muted">Staff</p>
            {isLoading ? (
              <p className="text-sm text-foreground-muted">Loading…</p>
            ) : (recipients ?? []).length === 0 ? (
              <p className="text-sm text-foreground-muted">There are no other staff to pick.</p>
            ) : (
              <ul className="max-h-56 space-y-1 overflow-y-auto">
                {(recipients ?? []).map((r) => (
                  <li key={r.id}>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-surface-sunken">
                      <input
                        type="checkbox"
                        checked={picked.has(r.id)}
                        onChange={() => toggle(r.id)}
                      />
                      {r.name}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Field label="Note (optional)">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why does it matter?"
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={picked.size === 0}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Mark important
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
